/*
  The HTTP client. One place that talks to the network, so the token rules and the error
  translation cannot be bypassed by a stray fetch() somewhere in a component.

  What it guarantees:

  * `credentials: 'same-origin'` on every request, so the HttpOnly refresh cookie rides
    along on the refresh call and nothing else has to know it exists.
  * The access token is read from the in-memory store at call time, never captured in a
    closure, so a rotation is picked up by requests already queued.
  * Refresh is single-flight. Refresh tokens rotate on use and a replayed rotated token is
    treated as theft (docs/07-identity-rbac.md I.1), so two concurrent 401s firing two
    refreshes would revoke the whole session lineage. Every caller awaits the same promise.
  * Exactly one retry after a successful refresh, and only for a request that had not
    already been retried. No retry loop can form.
*/

import { API_BASE_PATH } from './config';
import { ApiError, kindFromResponse, networkError, type ProblemDetails } from './errors';
import {
  clearAccessToken,
  getAccessToken,
  isAccessTokenStale,
  setAccessToken,
  type AccessToken,
} from './token-store';

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly signal?: AbortSignal;
  /**
   * Skip the access token and the refresh dance. Used by the auth endpoints themselves,
   * which are the only unauthenticated calls in the console.
   */
  readonly anonymous?: boolean;
}

interface RefreshResponseBody {
  readonly accessToken: string;
  readonly expiresAt: string;
}

let inFlightRefresh: Promise<AccessToken | null> | null = null;

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = new URL(`${API_BASE_PATH}${path}`, window.location.origin);
  if (query !== undefined) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return `${url.pathname}${url.search}`;
}

async function readProblem(response: Response): Promise<ProblemDetails> {
  try {
    const parsed: unknown = await response.json();
    if (parsed !== null && typeof parsed === 'object') {
      return parsed;
    }
  } catch {
    // A non-JSON error body (an edge proxy 502 page, for example) is not a client bug.
  }
  return { status: response.status, title: response.statusText };
}

/**
 * Exchange the HttpOnly refresh cookie for a new access token. Single-flight: concurrent
 * callers share one promise. Returns null when the cookie is gone or was rejected, which
 * means the technician is signed out.
 */
export async function refreshAccessToken(): Promise<AccessToken | null> {
  if (inFlightRefresh !== null) {
    return inFlightRefresh;
  }

  inFlightRefresh = (async (): Promise<AccessToken | null> => {
    try {
      const response = await fetch(buildUrl('/auth/refresh', undefined), {
        method: 'POST',
        // The refresh cookie is path-scoped to this endpoint and SameSite=Strict. This is
        // the only call that needs it, and it is why the console must be same-origin
        // with the API.
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        clearAccessToken();
        return null;
      }
      const body = (await response.json()) as RefreshResponseBody;
      const token: AccessToken = { value: body.accessToken, expiresAt: body.expiresAt };
      setAccessToken(token);
      return token;
    } catch {
      // A network failure is not proof the session is dead. Leave the existing token in
      // place and let the caller surface a transport error.
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

async function send(path: string, options: RequestOptions, isRetry: boolean): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.anonymous !== true) {
    if (isAccessTokenStale()) {
      await refreshAccessToken();
    }
    const token = getAccessToken();
    if (token !== null) {
      headers.Authorization = `Bearer ${token.value}`;
    }
  }

  let body: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      credentials: 'same-origin',
      headers,
      ...(body === undefined ? {} : { body }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    throw networkError(cause);
  }

  // One refresh and one retry, only for an authenticated request that has not retried yet.
  if (response.status === 401 && options.anonymous !== true && !isRetry) {
    const refreshed = await refreshAccessToken();
    if (refreshed !== null) {
      return send(path, options, true);
    }
  }

  return response;
}

export async function request<TResponse>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const response = await send(path, options, false);

  if (!response.ok) {
    const problem = await readProblem(response);
    throw new ApiError(kindFromResponse(response.status, problem), response.status, problem);
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  return (await response.json()) as TResponse;
}
